import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Document, Page } from "react-pdf";
import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
`;

const ViewerContainer = styled.div`
  display: flex;
  width: 100%;
  height: ${props => props.height || "698px"};
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const ThumbnailsContainer = styled.div`
  width: 200px;
  overflow-y: auto;
  border-right: 1px solid #e0e0e0;
  background-color: #f5f5f5;
  padding: 10px;
`;

const ThumbnailWrapper = styled.div`
  margin-bottom: 10px;
  cursor: pointer;
  border: ${props => props.isActive ? '1px solid #1976d2' : '1px solid transparent'};
  border-radius: 4px;
  padding: 2px;
  background-color: transparent;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1976d2;
    background-color: transparent;
  }
`;

const ThumbnailPage = styled(Page)`
  .react-pdf__Page__canvas {
    max-width: 100%;
    height: auto !important;
  }
`;

// Enhanced styled page for high-resolution rendering
const HighResPage = styled(Page)`
  .react-pdf__Page__canvas {
    /* Ensure canvas is rendered at device pixel ratio for crisp display */
    image-rendering: auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

const PageNumber = styled.div`
  text-align: center;
  font-size: 12px;
  margin-top: 4px;
  color: #666;
  font-weight: ${props => props.isActive ? '600' : '400'};
`;

const MainViewer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #fafafa;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const ViewerContent = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  min-height: 0;
`;

const PageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
`;

const HighlightOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 10;
`;

const HighlightBox = styled.div`
  position: absolute;
  background-color: rgba(255, 255, 93, 0.85);
  pointer-events: none;
  box-sizing: border-box;
  z-index: 999;
`;

const MainPage = styled(Page).withConfig({
  shouldForwardProp: (prop) => !['scale', 'panX', 'panY', 'isPanning', 'displayWidth', 'displayHeight'].includes(prop),
})`
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  transform: none;
  transition: none;
  transform-origin: center;
  flex-shrink: 0;
  will-change: auto;
  backface-visibility: hidden;
  -webkit-font-smoothing: subpixel-antialiased;
  cursor: ${props => props.scale > 1 ? (props.isPanning ? 'grabbing' : 'grab') : 'default'};
  user-select: none;
  
  /* Scale down the high-resolution canvas to display size for crisp rendering */
  ${props => props.displayWidth && props.displayHeight ? `
    width: ${props.displayWidth}px !important;
    height: ${props.displayHeight}px !important;
  ` : ''}
  
  /* High-resolution rendering optimizations */
  .react-pdf__Page__canvas {
    /* Ensure sharp rendering on high-DPI displays */
    -webkit-backface-visibility: hidden;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    /* Better text rendering */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    /* Scale canvas to fit container while maintaining quality */
    width: 100% !important;
    height: 100% !important;
    image-rendering: auto;
  }
`;

const PanWrapper = styled.div`
  transform: ${props => `translate3d(${props.panX}px, ${props.panY}px, 0)`};
  transition: ${props => props.isPanning ? 'none' : 'transform 0.15s ease-out'};
  transform-origin: center;
  will-change: transform;
  display: inline-block;
`;

const Controls = styled.div`
  padding: 10px 20px;
  border-top: 1px solid #e0e0e0;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const PageInfo = styled.div`
  font-size: 14px;
  color: #666;
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const NavButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #ccc;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #f0f0f0;
    border-color: #999;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ZoomLevel = styled.span`
  font-size: 12px;
  color: #666;
  min-width: 50px;
  text-align: center;
`;

const ControlButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #ccc;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s ease;
  min-width: 32px;

  &:hover:not(:disabled) {
    background-color: #f0f0f0;
    border-color: #999;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 16px;
  color: #666;
`;

const SkeletonContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  background-color: #fafafa;
`;

const SkeletonMainViewer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

const SkeletonViewerContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
`;

const SkeletonPage = styled.div`
  width: 400px;
  height: 566px;
  background: linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.7),
      transparent
    );
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }
`;

const SkeletonControls = styled.div`
  padding: 10px 20px;
  border-top: 1px solid #e0e0e0;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  flex-shrink: 0;
`;

const SkeletonControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SkeletonButton = styled.div`
  width: ${props => props.width || '60px'};
  height: 28px;
  background: linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.8),
      transparent
    );
    animation: ${shimmer} 1.2s ease-in-out infinite;
  }
`;

const SkeletonText = styled.div`
  width: ${props => props.width || '80px'};
  height: 16px;
  background: linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.8),
      transparent
    );
    animation: ${shimmer} 1s ease-in-out infinite;
  }
`;

const PdfViewerSkeleton = ({ height = "698px" }) => (
  <ViewerContainer height={height}>
    <SkeletonContainer>
      <SkeletonMainViewer>
        <SkeletonViewerContent>
          <SkeletonPage />
        </SkeletonViewerContent>
        
        <SkeletonControls>
          <SkeletonText width="100px" />
          
          <SkeletonControlGroup>
            <SkeletonButton width="32px" />
            <SkeletonText width="50px" />
            <SkeletonButton width="32px" />
            <SkeletonButton width="32px" />
            <SkeletonButton width="50px" />
          </SkeletonControlGroup>

          <SkeletonControlGroup>
            <SkeletonButton width="70px" />
            <SkeletonButton width="70px" />
          </SkeletonControlGroup>
        </SkeletonControls>
      </SkeletonMainViewer>
    </SkeletonContainer>
  </ViewerContainer>
);

const CustomPdfViewerComponent = ({ 
  documents, 
  activeDocument, 
  onDocumentChange, 
  activePageNumber = 1, 
  onPageChange,
  height = "698px",
  defaultZoom = 1,
  highlightData = null,
  allFieldHighlights = []
}) => {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(activePageNumber);
  const [scale, setScale] = useState(defaultZoom);
  const [rotationMap, setRotationMap] = useState(new Map());
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });

  const currentRotation = activeDocument ? (rotationMap.get(activeDocument.fileName) || 0) : 0;

  useEffect(() => {
    setCurrentPage(activePageNumber);
  }, [activePageNumber]);

  useEffect(() => {
    if (activeDocument && !rotationMap.has(activeDocument.fileName)) {
      setRotationMap(prevMap => {
        const newMap = new Map(prevMap);
        newMap.set(activeDocument.fileName, 0);
        return newMap;
      });
    }
  }, [activeDocument, rotationMap]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    if (currentPage > numPages) {
      setCurrentPage(1);
      if (onPageChange) onPageChange(1);
    }
  };

  const onPageLoadSuccess = (page) => {
    console.log("Page loaded successfully:", page);
    console.log("Page original dimensions:", { width: page.width, height: page.height });
    
    setPageSize({ 
      width: page.width, 
      height: page.height
    });
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    if (onPageChange) onPageChange(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      if (onPageChange) onPageChange(newPage);
    }
  };

  const handleNextPage = () => {
    if (currentPage < numPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      if (onPageChange) onPageChange(newPage);
    }
  };

  const handleZoomIn = useCallback(() => {
    setScale(prevScale => Math.min(prevScale + 0.25, 2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(prevScale => Math.max(prevScale - 0.25, 0.25));
  }, []);

  const handleRotate = useCallback(() => {
    if (activeDocument) {
      setRotationMap(prevMap => {
        const newMap = new Map(prevMap);
        const currentRotation = newMap.get(activeDocument.fileName) || 0;
        newMap.set(activeDocument.fileName, (currentRotation + 90) % 360);
        return newMap;
      });
    }
  }, [activeDocument]);

  const handleReset = useCallback(() => {
    setScale(defaultZoom);
    setPanX(0);
    setPanY(0);
    if (activeDocument) {
      setRotationMap(prevMap => {
        const newMap = new Map(prevMap);
        newMap.set(activeDocument.fileName, 0);
        return newMap;
      });
    }
  }, [defaultZoom, activeDocument]);

  const handleMouseDown = useCallback((e) => {
    if (scale > 1) { 
      setIsPanning(true);
      setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
      e.preventDefault();
    }
  }, [scale, panX, panY]);

  const handleMouseMove = useCallback((e) => {
    if (isPanning && scale > 1) {
      const newPanX = e.clientX - dragStart.x;
      const newPanY = e.clientY - dragStart.y;
      setPanX(newPanX);
      setPanY(newPanY);
      e.preventDefault();
    }
  }, [isPanning, scale, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (scale > 1 && e.touches.length === 1) {
      const touch = e.touches[0];
      setIsPanning(true);
      setDragStart({ x: touch.clientX - panX, y: touch.clientY - panY });
      e.preventDefault();
    }
  }, [scale, panX, panY]);

  const handleTouchMove = useCallback((e) => {
    if (isPanning && scale > 1 && e.touches.length === 1) {
      const touch = e.touches[0];
      const newPanX = touch.clientX - dragStart.x;
      const newPanY = touch.clientY - dragStart.y;
      setPanX(newPanX);
      setPanY(newPanY);
      e.preventDefault();
    }
  }, [isPanning, scale, dragStart]);

  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  useEffect(() => {
    if (scale <= 1) {
      setPanX(0);
      setPanY(0);
    }
  }, [scale]);

  const pdfBlob = useMemo(() => 
    activeDocument ? new Blob([activeDocument.blob], { type: "application/pdf" }) : null, 
    [activeDocument]
  );

  // Get device pixel ratio for high-resolution rendering
  const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  
  // Calculate rendered dimensions dynamically based on current scale and device pixel ratio
  const getRenderedDimensions = useCallback((pageSize) => {
    if (!pageSize.width || !pageSize.height) return { width: 0, height: 0 };
    
    const isLandscape = pageSize.width > pageSize.height;
    
    const baseDisplayWidth = isLandscape ? 600 : 400;
    
    const renderScale = Math.max(devicePixelRatio, 2);
    
    const aspectRatio = pageSize.height / pageSize.width;
    
    // Actual rendering width (canvas size - higher for sharp display)
    const renderWidth = baseDisplayWidth * scale * renderScale;
    const renderHeight = renderWidth * aspectRatio;
    
    // Display width (what CSS will show)
    const displayWidth = baseDisplayWidth * scale;
    const displayHeight = displayWidth * aspectRatio;
  
    return { 
      width: renderWidth,           // Canvas render size
      height: renderHeight,
      displayWidth: displayWidth,   // CSS display size
      displayHeight: displayHeight,
      baseWidth: baseDisplayWidth,
      baseHeight: baseDisplayWidth * aspectRatio
    };
  }, [scale, devicePixelRatio]);

  const calculateHighlightStyle = useCallback((highlightData, pageSize) => {
    console.log("calculateHighlightStyle called with:", { highlightData, pageSize, currentPage, scale, panX, panY });
    
    if (!highlightData || highlightData.pageNumber !== currentPage) {
      console.log("No highlight - missing data or wrong page:", { 
        hasData: !!highlightData, 
        highlightPage: highlightData?.pageNumber, 
        currentPage 
      });
      return null;
    }

    const renderedDims = getRenderedDimensions(pageSize);
    // Use display dimensions for highlight positioning (not render dimensions)
    const displayWidth = renderedDims.displayWidth || renderedDims.width;
    const displayHeight = renderedDims.displayHeight || renderedDims.height;
    
    const normalizedX = highlightData.x;
    const normalizedY = highlightData.y;
    const normalizedWidth = highlightData.width;
    const normalizedHeight = highlightData.height;

    // Calculate highlight position based on display dimensions
    const left = normalizedX * displayWidth;
    const top = normalizedY * displayHeight;
    const width = normalizedWidth * displayWidth;
    const height = normalizedHeight * displayHeight;

    const style = {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
    
    return style;
  }, [scale, currentPage, getRenderedDimensions, panX, panY]);

  if (!activeDocument) {
    return <PdfViewerSkeleton height={height} />;
  }

  return (
    <ViewerContainer height={height}>
      {/* <ThumbnailsContainer>
        <Document
          file={pdfBlob}
          onLoadSuccess={onDocumentLoadSuccess}
          renderAnnotationLayer={false}
          renderTextLayer={false}
        >
          {numPages > 0 && Array.from(new Array(numPages), (el, index) => (
            <ThumbnailWrapper
              key={`thumbnail_${index + 1}`}
              isActive={currentPage === index + 1}
              onClick={() => handlePageClick(index + 1)}
            >
              <ThumbnailPage
                pageNumber={index + 1}
                width={160}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
              <PageNumber isActive={currentPage === index + 1}>
                {index + 1}
              </PageNumber>
            </ThumbnailWrapper>
          ))}
        </Document>
      </ThumbnailsContainer> */}

      <MainViewer>
        <ViewerContent
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <PageContainer>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Document
                file={pdfBlob}
                onLoadSuccess={onDocumentLoadSuccess}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                options={{
                  cMapUrl: 'cmaps/',
                  cMapPacked: true,
                  standardFontDataUrl: 'standard_fonts/',
                  disableAutoFetch: false,
                  disableStream: false,
                  enableXfa: true,
                  useOnlyCssZoom: false,
                  disableRange: false,
                }}
              >
                <PanWrapper 
                  panX={panX}
                  panY={panY}
                  isPanning={isPanning}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                  style={{ position: 'relative' }}
                >
                  {(() => {
                    const renderedDims = pageSize.width > 0 ? getRenderedDimensions(pageSize) : { 
                      width: 400, 
                      height: 566,
                      displayWidth: 400,
                      displayHeight: 566
                    };
                    
                    return (
                      <>
                        <MainPage
                          pageNumber={currentPage}
                          width={renderedDims.width}
                          displayWidth={renderedDims.displayWidth}
                          displayHeight={renderedDims.displayHeight}
                          rotate={currentRotation}
                          onLoadSuccess={onPageLoadSuccess}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          canvasBackground="white"
                        />
                        {pageSize.width > 0 && (() => {
                          // Get all highlights for the current page (both single highlight and all field highlights)
                          const pageHighlights = [];
                          
                          if (highlightData && highlightData.pageNumber === currentPage) {
                            pageHighlights.push({ ...highlightData, isActive: true });
                          }
                          
                          const currentPageFields = allFieldHighlights.filter(h => h.pageNumber === currentPage);
                          pageHighlights.push(...currentPageFields.map(h => ({ ...h, isActive: false })));
                          
                          if (pageHighlights.length === 0) return null;
                          
                          return (
                            <HighlightOverlay style={{
                              width: `${renderedDims.displayWidth || renderedDims.width}px`,
                              height: `${renderedDims.displayHeight || renderedDims.height}px`,
                              transform: `rotate(${currentRotation}deg)`,
                              transformOrigin: 'center'
                            }}>
                              {pageHighlights.map((highlight, index) => {
                                const highlightStyle = calculateHighlightStyle(highlight, pageSize);
                                
                                return highlightStyle ? (
                                  <HighlightBox 
                                    key={`${highlight.id || index}-${highlight.type || 'highlight'}`}
                                    style={{
                                      ...highlightStyle,
                                      backgroundColor: highlight.isActive ? 'rgba(255, 255, 0, 0.5)' : 'transparent',
                                      border: highlight.isActive ? 'none' : '2px solid rgba(30, 144, 255, 0.6)',
                                    }} 
                                  />
                                ) : null;
                              })}
                            </HighlightOverlay>
                          );
                        })()}
                      </>
                    );
                  })()}
                </PanWrapper>
                </Document>
            </div>
          </PageContainer>
        </ViewerContent>

        <Controls>
          <PageInfo>
            Page {currentPage} of {numPages}
          </PageInfo>
          
          <ZoomControls>
            <ControlButton 
              onClick={handleZoomOut}
              disabled={scale <= 0.25}
              title="Zoom Out"
            >
              −
            </ControlButton>
            <ZoomLevel>
              {Math.round(scale * 100)}%
            </ZoomLevel>
            <ControlButton 
              onClick={handleZoomIn}
              disabled={scale >= 2}
              title="Zoom In"
            >
              +
            </ControlButton>
            <ControlButton 
              onClick={handleRotate}
              title="Rotate 90°"
            >
              ↻
            </ControlButton>
            <ControlButton 
              onClick={handleReset}
              title="Reset View"
            >
              Reset
            </ControlButton>
          </ZoomControls>

          <NavigationButtons>
            <NavButton 
              onClick={handlePrevPage} 
              disabled={currentPage <= 1}
            >
              Previous
            </NavButton>
            <NavButton 
              onClick={handleNextPage} 
              disabled={currentPage >= numPages}
            >
              Next
            </NavButton>
          </NavigationButtons>
        </Controls>
      </MainViewer>
    </ViewerContainer>
  );
};

export const CustomPdfViewer = React.memo(CustomPdfViewerComponent);
